import { AxiosInstance, AxiosRequestConfig } from "axios";

import { AxiosInstanceInterceptorRequestExtensionI } from "@/api/interceptors/interface";

/**
 * Team → Pipeline (C-16 S3, GLE-8410): Command Center's Pipeline page in the
 * app. Every route is behind the backend switches `user_roles_enabled` and
 * `user_roles_pipeline_enabled`: while either is off the route answers 404
 * with error_code 1035, which the page reads as "not switched on".
 *
 * A workspace's routes are an Admin's or that workspace's LGA's; the routes
 * without a workspace are the Admin "All workspaces" view. The server checks
 * every request; the app only hides what a user can't use. Money is USD and
 * GMV is the tier-aware customer charge (coordinator decision C16-D302).
 */

/** The card statuses (Command Center's meeting ladder). */
export const PIPELINE_STATUSES = {
  UPCOMING: "Upcoming",
  PENDING_FEEDBACK: "Pending Feedback",
  QUALIFIED: "Qualified",
  QUALIFIED_DISPUTED: "Qualified (Disputed)",
  SHOW: "Show",
  UNQUALIFIED: "Unqualified",
  DISQUALIFIED: "Disqualified",
  NO_SHOW: "No Show",
  CANCELED: "Canceled",
} as const;

export const PIPELINE_REMINDER_KINDS = {
  NO_SHOW: "no_show",
  REMINDER_CALL: "reminder_call",
  WARM_HANDOFF: "warm_handoff",
} as const;

export type PipelineReminderKindI =
  (typeof PIPELINE_REMINDER_KINDS)[keyof typeof PIPELINE_REMINDER_KINDS];

/** A card: Command Center's meeting_key. */
export interface PipelineCardKeyI {
  campaign_id: string;
  account_id: string;
  contact_id: string;
}

/** One meeting card, with the LGA marks on it. */
export interface PipelineCardI extends PipelineCardKeyI {
  campaign_name: string;
  group_id: string | null;
  /** "" when the campaign is in no group. */
  group_name: string;
  workspace_id: string;
  workspace: string;
  status: string;
  booked_at: string;
  meeting_date: string | null;
  booker_id: string;
  booker_name: string;
  booker_email: string;
  potential_total_payout: number;
  potential_user_payout: number;
  actual_total_payout: number;
  actual_user_payout: number;
  is_homefix: boolean;
  is_starred: boolean;
  is_trashed: boolean;
  last_reminder_kind: PipelineReminderKindI | null;
  last_reminded_at: string | null;
  /** "" when nobody reminded. */
  last_reminded_by_name: string;
}

/** The Recovery Queue or the Upcoming window; `as_of` is when the cards were read. */
export interface PipelineCardsResponseI {
  as_of: string;
  cards: Array<PipelineCardI>;
}

/** What a card shows beyond the card itself. */
export interface PipelineCardDetailsI extends PipelineCardKeyI {
  account_name: string;
  contact_name: string;
  /** The account's current owner; "" when nobody holds it. */
  owner_user_id: string;
  owner_name: string;
  next_touch_time: string | null;
  /** The priority list the dialer link opens; "" when there is none. */
  list_id: string;
  list_type: string;
  last_attempted_at: string | null;
  /**
   * The owner (else the booker) isn't working the campaign, or is an Admin
   * or an LGA: the "Not being worked" filter.
   */
  not_being_worked: boolean;
}

export interface PipelineCardDetailsResponseI {
  details: Array<PipelineCardDetailsI>;
}

/** The server reads at most this many cards' details per request. */
export const MAX_PIPELINE_CARD_DETAILS = 500;

/** A group's Pipeline settings (dates YYYY-MM-DD, money USD). */
export interface PipelineGroupSettingsI {
  is_active: boolean;
  is_hidden_from_sla: boolean;
  default_monthly_qm_target: number | null;
  monthly_bm_goal: number | null;
  base_gmv_per_qm: number | null;
  legacy_meta_qm_target: number | null;
  legacy_meta_base_gmv_per_qm: number | null;
  churned_at: string | null;
  go_live_date: string | null;
  order_form_signed_at: string | null;
  first_dial_at: string | null;
  first_meeting_booked_at: string | null;
  first_qm_at: string | null;
  last_dial_at: string | null;
  last_meeting_booked_at: string | null;
  last_qm_at: string | null;
  wind_down_notice_at: string | null;
  wind_down_effective_at: string | null;
  anticipated_renewal_date: string | null;
}

export interface PipelineMonthlyTargetI {
  month: string;
  qm_target: number;
  start_date_override: string | null;
}

/** A campaign group: Command Center's campaign, over platform campaigns. */
export interface PipelineGroupI {
  id: string;
  workspace_id: string | null;
  name: string;
  campaign_ids: Array<string>;
  settings: PipelineGroupSettingsI;
  targets: Array<PipelineMonthlyTargetI>;
}

export interface PipelineCampaignI {
  id: string;
  name: string;
  workspace_id: string | null;
  group_id: string | null;
  is_homefix: boolean;
}

export interface PipelineGroupsResponseI {
  groups: Array<PipelineGroupI>;
  campaigns: Array<PipelineCampaignI>;
}

/** A card's LGA marks after a write. */
export interface PipelineCardFlagsI extends PipelineCardKeyI {
  is_starred: boolean;
  is_trashed: boolean;
  last_reminder_kind: PipelineReminderKindI | null;
  last_reminded_at: string | null;
  last_reminded_by_name: string;
}

export interface PipelineCardFlagsResponseI {
  flags: PipelineCardFlagsI;
}

/** Star or unstar, trash or restore; an omitted field is left as it is. */
export interface SetPipelineCardFlagsRequestI extends PipelineCardKeyI {
  starred?: boolean;
  trashed?: boolean;
}

export interface RemindPipelineBookerRequestI extends PipelineCardKeyI {
  kind: PipelineReminderKindI;
}

// --- Analytics (C-16 S3 BE-2c: Command Center's maths, computed in Go) ---

/** CC's range toggle: -1 last month, 0 month to date, 1 last two months, else months back. */
export const ANALYTICS_RANGES = [-1, 0, 1, 3, 6, 12] as const;

export type AnalyticsRangeI = (typeof ANALYTICS_RANGES)[number];

export const ANALYTICS_GRANULARITIES = {
  WEEK: "week",
  MONTH: "month",
} as const;

export type AnalyticsGranularityI =
  (typeof ANALYTICS_GRANULARITIES)[keyof typeof ANALYTICS_GRANULARITIES];

export const ANALYTICS_SEGMENTS = {
  WORKSPACE: "workspace",
  CAMPAIGN: "campaign",
  REP: "rep",
} as const;

export type AnalyticsSegmentI =
  (typeof ANALYTICS_SEGMENTS)[keyof typeof ANALYTICS_SEGMENTS];

/** Command Center's workspace chips (the server's `NormalizeWorkspace`). */
export const ANALYTICS_WORKSPACE_CHIPS = [
  "Exodus Growth Partners",
  "Nurturance",
  "RevSprint",
  "Caret",
  "Glencoco",
] as const;

/** The chart controls; an omitted one takes the server's default (CC's). */
export interface PipelineAnalyticsParamsI {
  granularity?: AnalyticsGranularityI;
  range?: AnalyticsRangeI;
  segment?: AnalyticsSegmentI;
  grouped?: boolean;
  active_only?: boolean;
  exclude_cancels?: boolean;
  exclude_homefix?: boolean;
  /** Workspace chips (Admin, All workspaces); empty is every one. */
  workspaces?: Array<string>;
}

/** One period's rates (percent, null with nothing to divide by). */
export interface PipelineRatePointI {
  period: string;
  label: string;
  qual_rate: number | null;
  show_rate: number | null;
  bm_to_qm_rate: number | null;
  won: number;
  lost: number;
  fumbled: number;
  qm: number;
  total: number;
}

export interface PipelineRateSegmentI {
  segment: string;
  meetings: number;
  qual_rate: number | null;
  show_rate: number | null;
  bm_to_qm_rate: number | null;
  data: Array<PipelineRatePointI>;
}

export interface PipelineRatesResponseI {
  all: Array<PipelineRatePointI>;
  segments: Array<PipelineRateSegmentI>;
}

export interface PipelineQMPointI {
  period: string;
  label: string;
  count: number;
  gmv: number;
  user_payout: number;
}

export type PipelineProjectionConfidenceI = "low" | "medium" | "high";

/** The month's projected total, what remains on top of the month to date, P10/P90. */
export interface PipelineProjectionI {
  total: number;
  remaining: number;
  confidence: PipelineProjectionConfidenceI;
  p10: number;
  p90: number;
}

export interface PipelineQMProjectionI {
  count: PipelineProjectionI;
  gmv: PipelineProjectionI;
  user_payout: PipelineProjectionI;
}

export interface PipelineQMSegmentI {
  segment: string;
  count: number;
  gmv: number;
  user_payout: number;
  is_active_campaign: boolean;
  /** Months since the segment's first booked meeting (campaign segments). */
  age_months: number | null;
  upcoming_count: number;
  upcoming_gmv: number;
  upcoming_user_payout: number;
  data: Array<PipelineQMPointI>;
  projection: PipelineQMProjectionI | null;
}

export interface PipelineQMResponseI {
  all: Array<PipelineQMPointI>;
  /** The current month's bar (monthly view, current month in range). */
  projection: PipelineQMProjectionI | null;
  segments: Array<PipelineQMSegmentI>;
  rates: {
    show_rate: number;
    qual_rate_given_show: number;
    sample_size: number;
  };
}

/** One booker's no-shows and cancels on the Recovery Queue's cards. */
export interface PipelineNoShowRepI {
  name: string;
  count: number;
  gmv: number;
  user_payout: number;
}

export interface PipelineNoShowsResponseI {
  reps: Array<PipelineNoShowRepI>;
}

/** One name's GMV and its share of the total (percent). */
export interface PipelineShareI {
  name: string;
  gmv: number;
  percent: number;
}

/** The cumulative share after the index-th biggest name. */
export interface PipelineParetoPointI {
  index: number;
  name: string;
  gmv: number;
  cum_percent: number;
}

/** Shares biggest first, the long-tail curve, how many names make 80 % of GMV. */
export interface PipelineDistributionI {
  total: number;
  shares: Array<PipelineShareI>;
  pareto: Array<PipelineParetoPointI>;
  eighty_count: number;
}

/** Analytics → Outcome Dist.: Qualified and Show GMV, now and the period before. */
export interface PipelineOutcomeResponseI {
  by_campaign: PipelineDistributionI;
  by_rep: PipelineDistributionI;
  prior_by_campaign: PipelineDistributionI;
  prior_by_rep: PipelineDistributionI;
  prior_label: string;
}

/** One month of the SLA charts. */
export interface PipelineSLAMonthI {
  period: string;
  label: string;
  /** Qualified meetings over the summed targets, percent; null with no target. */
  attainment: number | null;
  /** Every counted QM, campaigns in no group included. */
  qm_count: number;
  /**
   * The QMs of groups with a target that month: attainment's numerator
   * (C16-D441; backend#6884, absent before it).
   */
  targeted_qm_count?: number;
  sla_target: number | null;
  avg_gmv: number;
  avg_user_payout: number;
  avg_gmv_per_campaign: number;
  avg_user_payout_per_campaign: number;
  active_campaigns: number;
  avg_gmv_per_rep: number;
  avg_user_payout_per_rep: number;
  active_reps: number;
}

/** A line of the SLA campaign table; unmapped = a campaign in no group. */
export interface PipelineSLARowI {
  key: string;
  name: string;
  group_id: string;
  is_unmapped: boolean;
  is_hidden: boolean;
  is_active: boolean;
  qm_count: number;
  /** QMs with an actual charge: the averages are over these. */
  settled: number;
  avg_gmv: number;
  avg_user_payout: number;
  total_gmv: number;
  total_user_payout: number;
  sla_target: number | null;
  fulfillment: number | null;
}

/** A line of the per-campaign or per-rep GMV tables. */
export interface PipelineSLAMoneyRowI {
  key: string;
  name: string;
  qm_count: number;
  /** QMs with an actual charge. */
  settled: number;
  total_gmv: number;
  total_user_payout: number;
  avg_gmv: number;
  avg_user_payout: number;
  is_unmapped: boolean;
  is_hidden: boolean;
}

export interface PipelineSLAResponseI {
  months: Array<PipelineSLAMonthI>;
  campaigns: Array<PipelineSLARowI>;
  campaign_gmv: Array<PipelineSLAMoneyRowI>;
  rep_gmv: Array<PipelineSLAMoneyRowI>;
}

/** The funnel's counts (Activity Volume). */
export interface PipelineActivityCountsI {
  calls: number;
  connects: number;
  conversations: number;
  convo_right_icp: number;
  booked_meetings: number;
}

/** CC's funnel rates, percent; null with nothing to divide by. */
export interface PipelineActivityRatesI {
  connect_rate: number | null;
  convo_rate: number | null;
  icp_rate: number | null;
  bm_rate: number | null;
}

export interface PipelineActivityPointI
  extends PipelineActivityCountsI, PipelineActivityRatesI {
  period: string;
  label: string;
}

/** A segment's drill-down row: a rep of a campaign, a campaign of a rep. */
export interface PipelineActivityBreakdownI
  extends PipelineActivityCountsI, PipelineActivityRatesI {
  name: string;
}

export interface PipelineActivitySegmentI
  extends PipelineActivityCountsI, PipelineActivityRatesI {
  segment: string;
  data: Array<PipelineActivityPointI>;
  /** Most calls first. */
  breakdown: Array<PipelineActivityBreakdownI>;
}

/** Analytics → Activity Volume: the daily rollup by ET week or month. */
export interface PipelineActivityResponseI {
  all: Array<PipelineActivityPointI>;
  segments: Array<PipelineActivitySegmentI>;
  /** The drill-down's dimension: rep for campaigns, else campaign. */
  breakdown_by: AnalyticsSegmentI;
}

export const PIPELINE_DRILL_DOWN_CONTEXTS = {
  RATES: "rates",
  NO_SHOWS: "noshows",
} as const;

export type PipelineDrillDownContextI =
  (typeof PIPELINE_DRILL_DOWN_CONTEXTS)[keyof typeof PIPELINE_DRILL_DOWN_CONTEXTS];

/** The meetings behind a Rates % segment or a rep's no-shows. */
export interface PipelineDrillDownParamsI extends PipelineAnalyticsParamsI {
  context: PipelineDrillDownContextI;
  /** The segment's or the rep's name. */
  value: string;
}

/** Newest meeting first: the first 500 of `total`. */
export interface PipelineDrillDownResponseI {
  as_of: string;
  total: number;
  cards: Array<PipelineCardI>;
}

/** One campaign group in one month of the Review grid (money USD). */
export interface PipelineReviewRowI {
  group_id: string;
  name: string;
  workspace_id: string;
  is_active: boolean;
  churned_at: string | null;
  /** Pro-rated by business days from the order form to the churn date. */
  target_qm: number;
  actual_qm: number;
  /** Booked meetings still ahead or without an outcome. */
  upcoming_bm: number;
  reps: number;
  base_gmv_per_qm: number | null;
  /** target_qm × base GMV per QM (CC: full month, not pro-rated). */
  target_gmv: number;
  captured_gmv: number;
  order_form_signed_at: string | null;
  first_dial_at: string | null;
  first_meeting_booked_at: string | null;
  first_qm_at: string | null;
  ttd_days: number | null;
  ttbm_days: number | null;
  ttqm_days: number | null;
  age_months: number | null;
  lga_names: Array<string>;
}

export interface PipelineReviewMonthI {
  /** "YYYY-MM". */
  month: string;
  is_current: boolean;
  /** 0 before the month, 1 after it, else today's share of its days. */
  elapsed_fraction: number;
  rows: Array<PipelineReviewRowI>;
}

/** The month before, the month and the month after. */
export interface PipelineReviewResponseI {
  months: Array<PipelineReviewMonthI>;
}

export interface CreatePipelineGroupRequestI {
  workspace_id: string | null;
  name: string;
  campaign_ids: Array<string>;
  settings: PipelineGroupSettingsI;
}

/**
 * A group change: an omitted field stays; `settings` replaces every
 * setting (null clears one).
 */
export interface UpdatePipelineGroupRequestI {
  name?: string;
  campaign_ids?: Array<string>;
  settings?: PipelineGroupSettingsI;
  archive?: boolean;
}

/** A month's target; a null qm_target clears the month. */
export interface PipelineMonthlyTargetInputI {
  month: string;
  qm_target: number | null;
  start_date_override: string | null;
}

export interface PipelineGroupResponseI {
  group: PipelineGroupI | null;
}

const analyticsParams = ({
  workspaces,
  ...rest
}: PipelineAnalyticsParamsI) => ({
  ...rest,
  workspaces: workspaces?.length ? workspaces.join(",") : undefined,
});

/** Errors of the Pipeline routes (backend errors.go 1300–1304). */
export const PIPELINE_ERROR_CODES = {
  BAD_INPUT: 1300,
  GROUP_CAMPAIGN: 1301,
  NOT_FOUND: 1302,
  GROUP_INPUT: 1303,
  REMINDER: 1304,
} as const;

/**
 * A 404 (+ 1035) means "switched off": an answer the page reads, not an
 * error to log. A fresh object per request, because the meta interceptor
 * writes into `meta` (as manage.ts does).
 */
const switchProbe = (
  config: AxiosRequestConfig = {}
): AxiosRequestConfig & AxiosInstanceInterceptorRequestExtensionI => ({
  ...config,
  meta: { expectedStatuses: [404] },
});

/** A workspace's routes, or the Admin "All workspaces" routes for null. */
const pipelinePath = (workspaceId: string | null, suffix: string) =>
  workspaceId
    ? `/v1/manage/workspace/${encodeURIComponent(workspaceId)}/pipeline${suffix}`
    : `/v1/manage/pipeline${suffix}`;

export const ManagePipelineAPIEndpoints = (api: AxiosInstance) => ({
  /** No Show and Canceled cards of active campaigns, Homefix out, newest first. */
  getQueue: (workspaceId: string | null) =>
    api.get<PipelineCardsResponseI>(
      pipelinePath(workspaceId, "/queue"),
      switchProbe()
    ),

  /**
   * Meetings on or after the earlier of `weekStart` (YYYY-MM-DD, read as ET
   * midnight; default this ET week's Monday) and a week ago, plus every
   * Pending Feedback card, soonest first. The page cuts its week in the
   * viewer's time (Command Center's local week) and asks from the day
   * before that Monday, which covers any time zone.
   */
  getUpcoming: (workspaceId: string | null, weekStart?: string) =>
    api.get<PipelineCardsResponseI>(
      pipelinePath(workspaceId, "/upcoming"),
      switchProbe({ params: { week_start: weekStart || undefined } })
    ),

  /** Up to MAX_PIPELINE_CARD_DETAILS cards; keys outside the scope are left out. */
  getCardDetails: (
    workspaceId: string | null,
    cards: Array<PipelineCardKeyI>
  ) =>
    api.post<PipelineCardDetailsResponseI>(
      pipelinePath(workspaceId, "/cards/details"),
      { cards }
    ),

  listGroups: (workspaceId: string | null) =>
    api.get<PipelineGroupsResponseI>(
      pipelinePath(workspaceId, "/groups"),
      switchProbe()
    ),

  setCardFlags: (
    workspaceId: string | null,
    body: SetPipelineCardFlagsRequestI
  ) =>
    api.put<PipelineCardFlagsResponseI>(
      pipelinePath(workspaceId, "/cards/flags"),
      body
    ),

  /** Analytics → Rates % (Command Center's builders, server-side). */
  getRates: (workspaceId: string | null, params: PipelineAnalyticsParamsI) =>
    api.get<PipelineRatesResponseI>(
      pipelinePath(workspaceId, "/analytics/rates"),
      switchProbe({ params: analyticsParams(params) })
    ),

  /** Analytics → QM Volume, with the current month's projection. */
  getQM: (workspaceId: string | null, params: PipelineAnalyticsParamsI) =>
    api.get<PipelineQMResponseI>(
      pipelinePath(workspaceId, "/analytics/qm"),
      switchProbe({ params: analyticsParams(params) })
    ),

  /** Analytics → Outcome Dist. (range, grouped, Homefix, workspace chips). */
  getOutcome: (workspaceId: string | null, params: PipelineAnalyticsParamsI) =>
    api.get<PipelineOutcomeResponseI>(
      pipelinePath(workspaceId, "/analytics/outcome"),
      switchProbe({ params: analyticsParams(params) })
    ),

  /** Analytics → SLA (range, Homefix, workspace chips). */
  getSLA: (workspaceId: string | null, params: PipelineAnalyticsParamsI) =>
    api.get<PipelineSLAResponseI>(
      pipelinePath(workspaceId, "/analytics/sla"),
      switchProbe({ params: analyticsParams(params) })
    ),

  /** Analytics → Activity Volume (the chart controls but No-shows only). */
  getActivity: (workspaceId: string | null, params: PipelineAnalyticsParamsI) =>
    api.get<PipelineActivityResponseI>(
      pipelinePath(workspaceId, "/analytics/activity"),
      switchProbe({ params: analyticsParams(params) })
    ),

  /** The drill-down sheet's meetings (CC's DrillDownSheet). */
  getDrillDown: (
    workspaceId: string | null,
    params: PipelineDrillDownParamsI
  ) =>
    api.get<PipelineDrillDownResponseI>(
      pipelinePath(workspaceId, "/analytics/cards"),
      switchProbe({ params: analyticsParams(params) })
    ),

  /** Review: the month (YYYY-MM, default this ET month) and its neighbours. */
  getReview: (workspaceId: string | null, month: string) =>
    api.get<PipelineReviewResponseI>(
      pipelinePath(workspaceId, "/review"),
      switchProbe({ params: { month } })
    ),

  createGroup: (
    workspaceId: string | null,
    body: CreatePipelineGroupRequestI
  ) =>
    api.post<PipelineGroupResponseI>(
      pipelinePath(workspaceId, "/groups"),
      body
    ),

  updateGroup: (
    workspaceId: string | null,
    groupId: string,
    body: UpdatePipelineGroupRequestI
  ) =>
    api.patch<PipelineGroupResponseI>(
      pipelinePath(workspaceId, `/groups/${encodeURIComponent(groupId)}`),
      body
    ),

  /** Up to 24 months of targets. */
  setGroupTargets: (
    workspaceId: string | null,
    groupId: string,
    targets: Array<PipelineMonthlyTargetInputI>
  ) =>
    api.put<PipelineGroupResponseI>(
      pipelinePath(
        workspaceId,
        `/groups/${encodeURIComponent(groupId)}/targets`
      ),
      { targets }
    ),

  /** "No-shows & cancels by rep": the queue's cards by booker. */
  getNoShowsByRep: (workspaceId: string | null, workspaces?: Array<string>) =>
    api.get<PipelineNoShowsResponseI>(
      pipelinePath(workspaceId, "/analytics/no_shows"),
      switchProbe({ params: analyticsParams({ workspaces }) })
    ),

  /** Bells the card's booker (NotificationType PipelineReminder). */
  remindBooker: (
    workspaceId: string | null,
    body: RemindPipelineBookerRequestI
  ) =>
    api.post<PipelineCardFlagsResponseI>(
      pipelinePath(workspaceId, "/cards/remind"),
      body
    ),
});
